import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms, SAFE_ZONE_VERTEX_CHUNK } from '../shaders/safeZone.glsl.js';
import { getSkyline, preloadSkyline } from '../skyline-assets.js';

// Skyline - a night skyline whose every building is an equaliser column.
// House, deep house, electronic pop, hip-hop, synthwave.
//
// THE DOUBLE READING. The viewer sees a skyline first, then understands that
// the buildings move with the music. The city comes from two photographs of
// the same view, windows off and windows on; tools/build-skyline.mjs cut
// their difference into windows, grouped the windows into 16 buildings by
// their x, and gave every window its HEIGHT inside its building (0 at the
// bottom, 1 at the top). The render is one rule per pixel:
//     lit    = height <= level(building)
//     colour = off + diff * lit * tint
// so a building lights from the bottom up to its level, and because the
// windows of one floor share a height, floor by floor - the segments of a
// vu-meter. The PEAK is a whole floor lit in the accent colour just above
// the level, hanging 800 ms before it falls: the vu-meter's peak dot, in the
// city. The river follows by construction: below the waterline every pixel
// carries the building above it with the mirrored height.
//
// Everything is in linear light (both textures sRGB, decoded by the GPU),
// so all levels at 1 give back the lit photograph exactly.

const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'skyline',
  family: 'photographic',
  energyLevel: 0.76,
  bpmMin: 95,
  bpmMax: 150,
  movement: 0.60,
  danceability: 0.80,
  tonality: 0.65,
  prefers: { lowEnd: [0.40, 1.0] },
  enabled: true
};

const DEFAULT_TIER = 2;
const TIER = {
  2: { streakTaps: 5 },
  1: { streakTaps: 3 },
  0: { streakTaps: 1 }
};
const LEVELS_W = 32;           // the level table, one texel per building id
const SAFE_FLOOR = 0.6;

// --- the equaliser ------------------------------------------------------------
// Three bands into 16 columns, bass on the left: wide gaussians, a +/-10%
// slow noise per building, EqRig's ballistics.
const BAND_CENTRES = [0.12, 0.50, 0.88];
const BAND_SIGMA = 0.26;
const DECORR = 0.10;
const FALL_PER_S = 1.6;        // linear fall, full scale per second
const PEAK_HOLD_S = 0.80;
const PEAK_FALL_PER_S = 1.0;
const KICK_SPIKE = 0.40;       // on the beat, the bass buildings jump
const KICK_SHARE = 0.34;       // of the buildings, from the left
const SWEEP_BEATS = 1.0;       // the bar: every building to the top, left to right, in one beat
const DROP_HOLD_BEATS = 1.0;   // the whole city lit
const DROP_SEARCH_BARS = 4;    // the searchlights
const GLOW_SMOOTH_S = 0.8;

const MODE_WARM = 0, MODE_ZONES = 1, MODE_PALETTE = 2;
const MODE_COUNT = 3;
const MODE_NAMES = { warm: MODE_WARM, zones: MODE_ZONES, palette: MODE_PALETTE };
const WARM = [1.0, 0.93, 0.80];
const ZONE_RED = [1.0, 0.22, 0.16];
const ACCENT = [1.0, 0.30, 0.20];

// --- the river ----------------------------------------------------------------
const RIPPLE_AMP = 0.006;      // uv, at the foreground edge
const STREAK = 0.012;          // vertical smear, uv, at the foreground edge
const REFL_GAIN = 0.92;

const SYNTH_BASE = 0.10, SYNTH_DECAY = 1.9;
const SYNTH_PERIOD_LOW = 4.7, SYNTH_PERIOD_MID = 3.1, SYNTH_PERIOD_HIGH = 6.3;
const SYNTH_BPM = 120;
const FALLBACK_COLORS = [0x40a0ff, 0xff50a0, 0xffc040];

function checkPalette(count) {
  let seed = 12345;
  const rnd = () => { seed = (seed * 1103515245 + 12345) & 0x7fffffff; return seed / 0x7fffffff; };
  const out = [];
  for (let id = 0; id <= count; id++) out.push([Math.floor(60 + rnd() * 195), Math.floor(60 + rnd() * 195), Math.floor(60 + rnd() * 195)]);
  return out;
}

const VERTEX = `
  varying vec2 vUv;
  varying vec4 vClip;
  void main() {
    vUv = uv;
    vec4 clip = vec4(position.xy, 0.0, 1.0);
    vClip = clip;
    gl_Position = clip;
  }
`;

const FRAGMENT = `
  precision mediump float;
  uniform sampler2D uOff;
  uniform sampler2D uDiff;
  uniform highp sampler2D uIds;
  uniform sampler2D uLevels;      // 32 x 3: row 0 = level, peak, floor; row 1 = palette colour; row 2 = check colour
  uniform float uMode;
  uniform vec3 uWarm;
  uniform vec3 uZoneA;
  uniform vec3 uZoneB;
  uniform vec3 uZoneC;
  uniform vec3 uAccent;
  uniform float uWater;           // the waterline, uv.y
  uniform float uTime;
  uniform float uRippleOn;
  uniform float uStreakTaps;
  uniform float uReflGain;
  uniform float uGlow;
  uniform float uSearch;          // 0..1, the searchlights' strength
  uniform vec4 uSearchA;          // origin xy, angle, phase
  uniform vec4 uSearchB;
  uniform vec2 uUvScale;
  uniform vec2 uUvOffset;
  uniform float uIdDebug;
  uniform float uExact;
  varying vec2 vUv;
  varying vec4 vClip;
${SAFE_ZONE_VERTEX_CHUNK}

  vec4 levelOf(float b) { return texture2D(uLevels, vec2((b + 0.5) / ${LEVELS_W}.0, 1.0 / 6.0)); }
  vec4 colorOf(float b) { return texture2D(uLevels, vec2((b + 0.5) / ${LEVELS_W}.0, 3.0 / 6.0)); }
  vec4 checkOf(float b) { return texture2D(uLevels, vec2((b + 0.5) / ${LEVELS_W}.0, 5.0 / 6.0)); }

  // A searchlight: a beam from a rooftop, sweeping; brightest near its
  // origin, fading along and across.
  float beam(vec2 p, vec4 s) {
    vec2 d = p - s.xy;
    float a = s.z + 0.55 * sin(uTime * 0.7 + s.w);
    vec2 dir = vec2(cos(a), sin(a));
    float along = dot(d, dir);
    float lateral = abs(d.x * dir.y - d.y * dir.x);
    if (along < 0.0) return 0.0;
    float w = 0.004 + 0.045 * along;
    return (1.0 - smoothstep(0.0, w, lateral)) * exp(-along * 2.2);
  }

  void main() {
    vec2 uv = vUv * uUvScale + uUvOffset;
    highp vec4 idc = texture2D(uIds, uv);
    highp float b = floor(idc.r * 255.0 + 0.5);
    float h = idc.g;
    float kind = idc.b;              // 1.0 window, ~0.5 reflection, <= 0.25 halo
    float refl = step(uv.y, uWater);
    float window = step(0.99, kind);
    float wv = refl > 0.5 ? 1.0 : (window > 0.5 ? 1.0 : kind / 0.251);   // the check picture's dimming
    float w = 1.0;   // the light itself is whole: a halo lights with its window, or all-at-1 is not the photograph

    vec4 L = levelOf(b);
    if (uIdDebug > 0.5) {
      vec3 c = checkOf(b).rgb;
      float k = window > 0.5 ? 1.0 : (refl > 0.5 ? (kind > 0.45 ? 0.55 : 0.12) : 0.08 + 0.3 * wv);
      c *= k;
      vec3 lo = c / 12.92;
      vec3 hi = pow((c + 0.055) / 1.055, vec3(2.4));
      gl_FragColor = vec4(mix(lo, hi, step(0.04045, c)), 1.0);
      return;
    }
    float level = L.r, peak = L.g, fl = L.b;
    // lit from the bottom up to the level; the peak's floor just above it
    float lit = step(h, level) * step(0.001, level);
    float band = step(peak - fl, h) * step(h, peak) * (1.0 - lit) * step(0.001, peak);

    // the tint: warm white, the vu-meter's zones by height, or the palette
    vec3 tint = uWarm;
    if (uMode > 0.5 && uMode < 1.5) tint = h < 0.5 ? uZoneA : (h < 0.8 ? uZoneB : uZoneC);
    else if (uMode > 1.5) tint = colorOf(b).rgb;

    // the river: the reflection is read through a rippled, smeared surface
    vec2 duv = vec2(0.0);
    float depth = refl > 0.5 ? clamp((uWater - uv.y) / max(uWater, 1e-3), 0.0, 1.0) : 0.0;
    if (uRippleOn > 0.5 && refl > 0.5) {
      float r = sin(uv.y * 38.0 + uTime * 0.7) + 0.6 * sin(uv.y * 21.0 - uTime * 0.45 + uv.x * 5.0);
      duv.x = r * ${RIPPLE_AMP.toFixed(4)} * (0.25 + depth);
    }
    vec3 diff;
    if (refl > 0.5 && uStreakTaps > 1.5) {
      // vertical streaks: the reflection smeared along y
      float step1 = ${STREAK.toFixed(4)} * (0.2 + depth) / uStreakTaps;
      diff = vec3(0.0);
      float n = 0.0;
      for (int i = -2; i <= 2; i++) {
        if (abs(float(i)) * 2.0 > uStreakTaps) continue;
        diff += texture2D(uDiff, uv + duv + vec2(0.0, float(i) * step1)).rgb;
        n += 1.0;
      }
      diff /= n;
    } else {
      diff = texture2D(uDiff, uv + duv).rgb;
    }
    vec3 off = texture2D(uOff, uv).rgb;
    vec3 light = diff * (lit * tint + band * uAccent * 1.15) * w;
    light *= mix(1.0, uReflGain, refl);
    vec3 col = off + light;

    // the sky glow: the city's own light on the clouds, following the mean level
    float sky = (1.0 - refl) * (1.0 - wv) * smoothstep(0.45, 0.95, uv.y);
    col += vec3(0.28, 0.22, 0.30) * uGlow * sky * 0.35;

    // the searchlights (the drop), in the air above the city and faintly in the water
    if (uSearch > 0.001) {
      float bm = beam(uv, uSearchA) + beam(uv, uSearchB);
      vec2 m = vec2(uv.x, 2.0 * uWater - uv.y);
      float bmr = (beam(m, uSearchA) + beam(m, uSearchB)) * 0.35;
      col += vec3(0.75, 0.82, 1.0) * uSearch * (bm * (1.0 - refl) * (1.0 - 0.7 * wv) + bmr * refl);
    }

    float att = safeZoneAttenuation(vClip);
    att = mix(${SAFE_FLOOR.toFixed(2)}, 1.0, clamp((att - 0.32) / 0.68, 0.0, 1.0));
    att = mix(att, 1.0, uExact);
    gl_FragColor = vec4(col * att, 1.0);
  }
`;

function urlParam(name) {
  if (typeof window === 'undefined' || !window.location) return null;
  return new URLSearchParams(window.location.search).get(name);
}
function clamp01(v) { return v < 0 ? 0 : (v > 1 ? 1 : v); }
function lerp(a, b, t) { return a + (b - a) * t; }

export class SkylineVisualizer extends Visualizer {
  constructor(options) {
    super();
    const tier = options && options.qualityTier != null ? options.qualityTier : DEFAULT_TIER;
    this._tier = TIER[tier] ? tier : DEFAULT_TIER;
    this._layout = TIER[this._tier];
    // ?skylineid=1 draws the build's check picture (a colour per building).
    this._idDebug = urlParam('skylineid') === '1';
    // ?skylinelevel=<f> pins every building's level: 1 is the lit
    // photograph, 0 the dark one. ?skylinebuilding=<n>,<f> pins one
    // building's level, the others at 0.
    const all = urlParam('skylinelevel');
    this._allDebug = all != null && all !== '' && Number.isFinite(parseFloat(all)) ? parseFloat(all) : null;
    const one = urlParam('skylinebuilding');
    this._oneDebug = null;
    if (one) { const q = one.split(',').map(parseFloat); if (q.length === 2 && q.every(Number.isFinite)) this._oneDebug = { id: q[0], level: q[1] }; }
    const m = urlParam('skylinemode');
    this._forcedMode = m != null && MODE_NAMES[m] !== undefined ? MODE_NAMES[m] : null;
    this._probe = urlParam('skylineprobe') === '1';
    const exact = this._idDebug || this._allDebug != null || this._oneDebug != null;
    this._exact = exact;
    this._postProcessing = exact
      ? { bloomStrength: 0, vignette: 0, grain: 0, aberration: 0, toneMapping: THREE.NoToneMapping, toneMappingExposure: 1 }
      : { bloomStrength: 0.40, vignette: 0.25, aberration: 0.001 };
  }

  get postProcessing() { return this._postProcessing; }

  init(scene, camera) {
    this._scene = scene;
    this._camera = camera;
    const assets = getSkyline();
    this._assets = assets;
    if (!assets) {
      const self = this;
      preloadSkyline().then(function (a) {
        if (!a || !self._scene || self._assets) return;
        const s = self._scene, c = self._camera;
        self.dispose();
        self.init(s, c);
      });
    }
    const data = assets ? assets.data : { count: 0, size: [1376, 768], waterline: 0.585, buildings: [] };
    this._count = data.count;
    this._imageAspect = data.size[0] / data.size[1];
    this._buildings = data.buildings;
    const n = this._count;
    this._target = new Float32Array(n + 1);
    this._level = new Float32Array(n + 1);
    this._peak = new Float32Array(n + 1);
    this._peakHold = new Float32Array(n + 1);
    this._noisePhase = new Float32Array(n + 1);
    this._noiseRate = new Float32Array(n + 1);
    for (let b = 1; b <= n; b++) { this._noisePhase[b] = Math.random() * Math.PI * 2; this._noiseRate[b] = (0.35 + 0.17 * (b % 5)) * (b % 2 ? 1 : -1); }

    // --- the level table: row 0 level/peak/floor, row 1 colour
    this._levelsData = new Float32Array(LEVELS_W * 3 * 4);
    const pal = checkPalette(n);
    for (let b = 0; b < LEVELS_W; b++) {
      const bd = this._buildings[b - 1];
      this._levelsData[b * 4 + 2] = bd ? bd.floor : 0.03;
      this._levelsData[b * 4 + 3] = 1;
      const c = pal[b] || [0, 0, 0];
      for (const row of [1, 2]) {
        const o = (LEVELS_W * row + b) * 4;
        this._levelsData[o] = c[0] / 255; this._levelsData[o + 1] = c[1] / 255; this._levelsData[o + 2] = c[2] / 255; this._levelsData[o + 3] = 1;
      }
    }
    this._levelsTex = new THREE.DataTexture(this._levelsData, LEVELS_W, 3, THREE.RGBAFormat, THREE.FloatType);
    this._levelsTex.minFilter = THREE.NearestFilter;
    this._levelsTex.magFilter = THREE.NearestFilter;
    this._levelsTex.generateMipmaps = false;
    this._levelsTex.needsUpdate = true;
    this._checkPalette = pal;

    this._uniforms = Object.assign({
      uOff: { value: assets ? assets.off : null },
      uDiff: { value: assets ? assets.diff : null },
      uIds: { value: assets ? assets.ids : null },
      uLevels: { value: this._levelsTex },
      uMode: { value: this._forcedMode != null ? this._forcedMode : MODE_WARM },
      // the exact views add the photograph's own light, untinted
      uWarm: { value: this._exact ? new THREE.Vector3(1, 1, 1) : new THREE.Vector3(WARM[0], WARM[1], WARM[2]) },
      uZoneA: { value: new THREE.Vector3(0.4, 0.8, 1) },
      uZoneB: { value: new THREE.Vector3(1, 0.8, 0.3) },
      uZoneC: { value: new THREE.Vector3(ZONE_RED[0], ZONE_RED[1], ZONE_RED[2]) },
      uAccent: { value: new THREE.Vector3(ACCENT[0], ACCENT[1], ACCENT[2]) },
      uWater: { value: 1 - data.waterline },
      uTime: { value: 0 },
      uRippleOn: { value: this._exact ? 0 : 1 },
      uStreakTaps: { value: this._exact ? 1 : this._layout.streakTaps },
      uReflGain: { value: this._exact ? 1 : REFL_GAIN },
      uGlow: { value: 0 },
      uSearch: { value: 0 },
      uSearchA: { value: new THREE.Vector4(0.3, 0.7, 1.3, 0) },
      uSearchB: { value: new THREE.Vector4(0.7, 0.72, 1.9, 2.1) },
      uUvScale: { value: new THREE.Vector2(1, 1) },
      uUvOffset: { value: new THREE.Vector2(0, 0) },
      uIdDebug: { value: this._idDebug ? 1 : 0 },
      uExact: { value: this._exact ? 1 : 0 }
    }, safeZoneUniforms);
    this._material = new THREE.ShaderMaterial({ uniforms: this._uniforms, vertexShader: VERTEX, fragmentShader: FRAGMENT, depthTest: false, depthWrite: false });
    this._geometry = new THREE.PlaneGeometry(2, 2);
    this._quad = new THREE.Mesh(this._geometry, this._material);
    this._quad.frustumCulled = false;
    this._quad.renderOrder = -10;
    scene.add(this._quad);

    // the searchlights' rooftops: two tall buildings, a third in from each side
    if (n >= 4) {
      const pick = (i) => { const bd = this._buildings[i]; return [(bd.x0 + bd.x1) / 2, 1 - bd.top]; };
      const a = pick(Math.floor(n * 0.3)), c = pick(Math.floor(n * 0.7));
      this._uniforms.uSearchA.value.set(a[0], a[1], 1.25, 0);
      this._uniforms.uSearchB.value.set(c[0], c[1], 1.85, 2.1);
    }

    this._time = 0;
    this._step = 0;
    this._sinceStep = 0;
    this._beatPeriod = 60 / SYNTH_BPM;
    this._lastBeatPhase = null;
    this._lastBarPhase = null;
    this._lastPhrasePhase = null;
    this._lastMode = null;
    this._mode = this._forcedMode != null ? this._forcedMode : MODE_WARM;
    this._kick = 0;
    this._sweepT = 99;
    this._dropHoldUntil = -1;
    this._searchUntil = -1;
    this._glow = 0;
    this._bands = { low: 0.5, mid: 0.5, high: 0.5 };
    this._synth = { low: SYNTH_BASE, mid: SYNTH_BASE, high: SYNTH_BASE };
    this._synthPhase = { low: null, mid: null, high: null };
    this._lastPaletteRef = undefined;
    this._aspect = 0;
    this._scratch = new THREE.Color();
    this._hsl = { h: 0, s: 0, l: 0 };
    this._applyPalette(null);
    this._reframe();
    this._writeLevels();
  }

  _reframe() {
    const aspect = safeZoneUniforms.uAspect.value || 16 / 9;
    this._aspect = aspect;
    const u = this._uniforms;
    if (aspect > this._imageAspect) { const k = this._imageAspect / aspect; u.uUvScale.value.set(1, k); u.uUvOffset.value.set(0, (1 - k) / 2); }
    else { const k = aspect / this._imageAspect; u.uUvScale.value.set(k, 1); u.uUvOffset.value.set((1 - k) / 2, 0); }
  }

  // The palette: ZONES takes two colours for the low and middle floors (the
  // top stays red, a vu-meter's red); PALETTE gives every building one of
  // the vivid colours.
  _applyPalette(palette) {
    const vivid = palette && palette.vividColors && palette.vividColors.length ? palette.vividColors : FALLBACK_COLORS;
    const norm = (hex) => { this._scratch.set(hex); this._scratch.getHSL(this._hsl); this._scratch.setHSL(this._hsl.h, Math.max(0.5, this._hsl.s), 0.6); const mx = Math.max(this._scratch.r, this._scratch.g, this._scratch.b) || 1; return [this._scratch.r / mx, this._scratch.g / mx, this._scratch.b / mx]; };
    const a = norm(vivid[0]), b = norm(vivid[1 % vivid.length]);
    this._uniforms.uZoneA.value.set(a[0], a[1], a[2]);
    this._uniforms.uZoneB.value.set(b[0], b[1], b[2]);
    for (let i = 1; i <= this._count; i++) {
      const c = norm(vivid[(i - 1) % vivid.length]);
      const o = (LEVELS_W + i) * 4;
      this._levelsData[o] = c[0]; this._levelsData[o + 1] = c[1]; this._levelsData[o + 2] = c[2];
    }
    this._levelsTex.needsUpdate = true;
  }

  // --- the data: 16 columns from three bands --------------------------------------
  _reconstruct(bands, dt) {
    const n = this._count;
    const bv = [bands.low, bands.mid, bands.high];
    const sweep = this._sweepT < SWEEP_BEATS ? this._sweepT / SWEEP_BEATS : -1;
    const dropHold = this._time < this._dropHoldUntil;
    for (let b = 1; b <= n; b++) {
      const x = n > 1 ? (b - 1) / (n - 1) : 0.5;
      let v = 0, ws = 0;
      for (let i = 0; i < 3; i++) { const d = (x - BAND_CENTRES[i]) / BAND_SIGMA; const w = Math.exp(-d * d); v += w * bv[i]; ws += w; }
      v = ws > 0 ? v / ws : 0;
      this._noisePhase[b] += dt * this._noiseRate[b];
      v *= 1 + DECORR * Math.sin(this._noisePhase[b]);
      if (this._kick > 0 && x < KICK_SHARE) v += KICK_SPIKE * this._kick * (1 - x / KICK_SHARE);
      // the bar sweep: to the top, left to right, in one beat
      if (sweep >= 0 && sweep * (n + 1) >= b) v = 1;
      if (dropHold) v = 1;
      this._target[b] = clamp01(v);
    }
    this._kick = Math.max(0, this._kick - dt * 10);
  }

  // Instant attack, linear fall, a peak that hangs 800 ms.
  _ballistics(dt) {
    const n = this._count;
    for (let b = 1; b <= n; b++) {
      const t = this._target[b];
      let v = this._level[b];
      if (t >= v) v = t; else v = Math.max(t, v - FALL_PER_S * dt);
      this._level[b] = v;
      if (v >= this._peak[b]) { this._peak[b] = v; this._peakHold[b] = PEAK_HOLD_S; }
      else if (this._peakHold[b] > 0) this._peakHold[b] -= dt;
      else this._peak[b] = Math.max(v, this._peak[b] - PEAK_FALL_PER_S * dt);
    }
  }

  _writeLevels() {
    const d = this._levelsData;
    let sum = 0;
    for (let b = 1; b <= this._count; b++) {
      let lv = this._level[b], pk = this._peak[b];
      if (this._allDebug != null) { lv = this._allDebug; pk = this._allDebug; }
      else if (this._oneDebug) { lv = b === this._oneDebug.id ? this._oneDebug.level : 0; pk = lv; }
      d[b * 4] = lv; d[b * 4 + 1] = pk;
      sum += lv;
    }
    d[0] = 0; d[1] = 0;
    this._levelsTex.needsUpdate = true;
    this._meanLevel = this._count ? sum / this._count : 0;
  }

  _stepSynthBands(deltaTime, profile) {
    const k = Math.exp(-SYNTH_DECAY * deltaTime);
    const sy = this._synth;
    sy.low = SYNTH_BASE + (sy.low - SYNTH_BASE) * k;
    sy.mid = SYNTH_BASE + (sy.mid - SYNTH_BASE) * k;
    sy.high = SYNTH_BASE + (sy.high - SYNTH_BASE) * k;
    const fired = (key, phase) => { const prev = this._synthPhase[key]; this._synthPhase[key] = phase; return prev != null && phase < prev; };
    const bar = profile.barPhase, beat = profile.beatPhase;
    if (bar != null && beat != null) {
      if (fired('low', beat)) sy.low = 1.0;
      if (fired('mid', (bar + 0.5) % 1)) sy.mid = 0.85;
      if (fired('high', (beat + 0.5) % 1)) sy.high = 0.75;
      return;
    }
    const t = this._time;
    if (fired('low', (t / SYNTH_PERIOD_LOW) % 1)) sy.low = 1.0;
    if (fired('mid', (t / SYNTH_PERIOD_MID) % 1)) sy.mid = 0.85;
    if (fired('high', (t / SYNTH_PERIOD_HIGH) % 1)) sy.high = 0.75;
  }

  _effectiveBands(director) {
    if (director && director.hasReliableGrid && director.bands) return director.bands;
    return this._synth;
  }

  _onBeat() { this._step++; this._sinceStep = 0; this._kick = 1; }
  _onBar() { this._sweepT = 0; }
  _onPhrase() {
    if (this._forcedMode != null) return;
    this._mode = (this._mode + 1 + Math.floor(Math.random() * (MODE_COUNT - 1))) % MODE_COUNT;
  }
  _onDrop() {
    this._dropHoldUntil = this._time + DROP_HOLD_BEATS * this._beatPeriod;
    this._searchUntil = this._time + DROP_SEARCH_BARS * 4 * this._beatPeriod;
    for (let b = 1; b <= this._count; b++) { this._level[b] = 1; this._peak[b] = 1; this._peakHold[b] = PEAK_HOLD_S; }
  }

  update(deltaTime, profile) {
    const dt = Math.min(deltaTime, 0.05);
    const playing = profile.playbackState === 'PLAYING';
    const director = profile.director;
    const u = this._uniforms;

    if (profile.artworkPalette !== this._lastPaletteRef) {
      this._lastPaletteRef = profile.artworkPalette;
      this._applyPalette(profile.artworkPalette);
    }
    const aspect = safeZoneUniforms.uAspect.value || 0;
    if (aspect && aspect !== this._aspect) this._reframe();

    if (!playing) {
      // paused: the levels fall, the river keeps moving
      for (let b = 1; b <= this._count; b++) this._target[b] = 0;
      this._ballistics(dt);
      this._writeLevels();
      return;
    }

    this._time += dt;
    this._sinceStep += dt;
    const bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || SYNTH_BPM;
    this._beatPeriod = 60 / bpm;
    this._stepSynthBands(dt, profile);
    const bands = this._effectiveBands(director);
    this._bands.low = bands && bands.low != null ? bands.low : 0.5;
    this._bands.mid = bands && bands.mid != null ? bands.mid : 0.5;
    this._bands.high = bands && bands.high != null ? bands.high : 0.5;

    const beatPhase = director && director.beat != null ? director.beat : profile.beatPhase;
    if (beatPhase != null) {
      if (this._lastBeatPhase != null && beatPhase < this._lastBeatPhase) this._onBeat();
      this._lastBeatPhase = beatPhase;
    } else if (this._sinceStep >= this._beatPeriod) this._onBeat();
    const barPhase = director && director.bar != null ? director.bar : profile.barPhase;
    if (barPhase != null) {
      if (this._lastBarPhase != null && barPhase < this._lastBarPhase) this._onBar();
      this._lastBarPhase = barPhase;
    } else if (this._sinceStep === 0 && this._step % 4 === 0) this._onBar();
    const phrasePhase = director && director.phrase != null ? director.phrase : profile.phrasePhase;
    if (phrasePhase != null) {
      if (this._lastPhrasePhase != null && phrasePhase < this._lastPhrasePhase) this._onPhrase();
      this._lastPhrasePhase = phrasePhase;
    } else if (this._sinceStep === 0 && this._step > 0 && this._step % 16 === 0) this._onPhrase();
    const mode = director ? director.mode : null;
    if (mode === 'drop' && this._lastMode !== 'drop') this._onDrop();
    this._lastMode = mode;

    if (this._sweepT < SWEEP_BEATS) this._sweepT += dt / this._beatPeriod;
    this._reconstruct(this._bands, dt);
    this._ballistics(dt);
    this._writeLevels();

    this._glow += (this._meanLevel - this._glow) * Math.min(1, dt / GLOW_SMOOTH_S);
    u.uGlow.value = this._exact ? 0 : this._glow;
    u.uMode.value = this._exact ? MODE_WARM : this._mode;
    u.uTime.value = this._time % 64;
    const search = this._time < this._searchUntil ? clamp01((this._searchUntil - this._time) / (this._beatPeriod * 2)) : 0;
    u.uSearch.value = this._exact ? 0 : search;

    if (this._probe && typeof window !== 'undefined') window.__skyline = this.getProbe();
  }

  getProbe() {
    return {
      t: this._time, step: this._step, mode: this._mode, mean: this._meanLevel, glow: this._glow,
      search: this._uniforms.uSearch.value, sweepT: this._sweepT,
      target: Array.from(this._target.slice(1)), level: Array.from(this._level.slice(1)), peak: Array.from(this._peak.slice(1)),
      floors: this._buildings.map((b) => b.floor)
    };
  }

  dispose() {
    if (this._quad) this._scene.remove(this._quad);
    if (this._geometry) this._geometry.dispose();
    if (this._material) this._material.dispose();
    if (this._levelsTex) this._levelsTex.dispose();
    this._quad = null;
    if (typeof window !== 'undefined' && window.__skyline) delete window.__skyline;
  }
}
